'use client';

import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { useRef, type ReactNode } from 'react';

import { Button } from '@platform/ui/components/button';
import { useDialogFocus } from '@platform/ui/lib/dialog-focus';
import { cn } from '@platform/ui/lib/utils';

export interface DrawerProps {
    open: boolean;
    onClose: () => void;
    title: ReactNode;
    description?: ReactNode;
    children: ReactNode;
    /** The one primary button, and its alternatives, pinned under the body. */
    footer?: ReactNode;
    /** The close button's accessible name, in the app's language. */
    closeLabel: string;
    /**
     * Navigation opens from the left, where the sidebar lives; work from the
     * right; a choice made on a phone — a shop's filters, its sort — rises from
     * the bottom.
     */
    side?: 'left' | 'right' | 'bottom';
    /** A right-hand drawer's width: a form's 608px, or 400px for a list such as a shop's basket. */
    size?: 'md' | 'sm';
    /** Drops the body's padding, for rows that draw their own edges. */
    flush?: boolean;
}

/**
 * Where writes happen (docs/11 §1): a panel from the right, 608px wide and full
 * screen on a phone, with the page still behind it. Focus starts in the first
 * field and stays inside; Escape and the scrim close it, and the focus returns
 * to what opened it.
 */
export function Drawer({ open, onClose, title, description, children, footer, closeLabel, side = 'right', size = 'md', flush }: DrawerProps) {
    const content = useRef<HTMLDivElement>(null);
    const focus = useDialogFocus(content);

    return (
        <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    ref={content}
                    {...(description ? {} : { 'aria-describedby': undefined })}
                    onOpenAutoFocus={focus.onOpenAutoFocus}
                    onCloseAutoFocus={focus.onCloseAutoFocus}
                    className={cn(
                        'fixed z-50 flex flex-col border-border bg-card text-card-foreground shadow-overlay outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0',
                        side === 'right' && 'inset-y-0 right-0 size-full border-l data-[state=open]:slide-in-from-right-4',
                        side === 'right' && (size === 'sm' ? 'sm:max-w-100' : 'sm:max-w-152'),
                        side === 'left' && 'inset-y-0 left-0 h-full w-80 max-w-[88%] border-r data-[state=open]:slide-in-from-left-4',
                        side === 'bottom' && 'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-overlay border-t data-[state=open]:slide-in-from-bottom-4',
                    )}
                >
                    <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-6 py-5">
                        <div className="min-w-0">
                            <DialogPrimitive.Title className="text-18 font-semibold tracking-heading">{title}</DialogPrimitive.Title>
                            {description && (
                                <DialogPrimitive.Description className="mt-1 text-13 text-muted-foreground">
                                    {description}
                                </DialogPrimitive.Description>
                            )}
                        </div>
                        <DialogPrimitive.Close asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={closeLabel} className="-mr-2">
                                <X aria-hidden="true" />
                            </Button>
                        </DialogPrimitive.Close>
                    </header>
                    <div className={cn('min-h-0 flex-1 overflow-y-auto', !flush && 'p-6')}>{children}</div>
                    {footer && (
                        <footer className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-border bg-muted px-6 py-4">
                            {footer}
                        </footer>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
